import { useMemo, useRef, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Input,
  Spinner,
} from "@/components";
import type {
  AdminSentencePuzzle,
  AssignSentencePuzzlesResult,
  BulkImportSentencePuzzlesResult,
  DeleteAllSentencePuzzlesResult,
  DeleteDraftSentencePuzzlesResult,
  PublishSentencePuzzlesResult,
  SentencePuzzleListItem,
  StaticTermSheetListItem,
  TermSheetListItem,
  UnpublishSentencePuzzlesResult,
} from "@/lib/admin-types";
import { GAME_TYPE_BUILD_SENTENCE } from "@/lib/admin-types";
import { apiJson, getApiErrorMessage } from "@/lib/api";
import { formatAdminDateTime } from "@/lib/format";
import {
  cloudTermSheetResourceKey,
  termSheetPracticeSearch,
} from "@/lib/term-sheet-practice";

const DELETE_ALL_CONFIRM_PHRASE = "DELETE ALL";

type BankFilter = "bank" | "assigned" | "all";

type TermSheetOption = {
  value: string;
  label: string;
  group: string;
};

const CSV_TEMPLATE = `english,hmong,distractors,resource_key,game_type
We go to the store.,Peb mus tom khw.,khw|tom,,
I go home.,Kuv mus tsev.,,,
The children are playing.,Cov menyuam ua si.,,,
`;

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sentence-puzzles-template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function SentencePuzzlesList() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteDraftsOpen, setDeleteDraftsOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  const [manualEnglish, setManualEnglish] = useState("");
  const [manualHmong, setManualHmong] = useState("");
  const [manualDistractors, setManualDistractors] = useState("");
  const [manualResourceKey, setManualResourceKey] = useState("");
  const [manualGameType, setManualGameType] = useState("");

  const [bankFilter, setBankFilter] = useState<BankFilter>("bank");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignResourceKey, setAssignResourceKey] = useState("");
  const [assignPublish, setAssignPublish] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [autoDistractors, setAutoDistractors] = useState(false);

  const listQ = useQuery({
    queryKey: ["admin", "sentence-puzzles"],
    queryFn: () =>
      apiJson<SentencePuzzleListItem[]>("/v1/admin/sentence-puzzles"),
  });

  const cloudSheetsQ = useQuery({
    queryKey: ["admin", "term-sheets"],
    queryFn: () => apiJson<TermSheetListItem[]>("/v1/admin/term-sheets"),
  });

  const staticSheetsQ = useQuery({
    queryKey: ["admin", "term-sheets", "static"],
    queryFn: () =>
      apiJson<StaticTermSheetListItem[]>("/v1/admin/term-sheets/static"),
  });

  const termSheetOptions = useMemo((): TermSheetOption[] => {
    const options: TermSheetOption[] = [];
    for (const sheet of staticSheetsQ.data ?? []) {
      options.push({
        value: sheet.resource_key,
        label: sheet.title,
        group: `Static · ${sheet.category || "Other"}`,
      });
    }
    for (const sheet of cloudSheetsQ.data ?? []) {
      options.push({
        value: cloudTermSheetResourceKey(sheet.slug),
        label: sheet.name,
        group: `Cloud · ${sheet.category || "Other"}`,
      });
    }
    return options.sort((a, b) => {
      const groupCmp = a.group.localeCompare(b.group);
      if (groupCmp !== 0) return groupCmp;
      return a.label.localeCompare(b.label);
    });
  }, [cloudSheetsQ.data, staticSheetsQ.data]);

  const optionGroups = useMemo(() => {
    const groups = new Map<string, TermSheetOption[]>();
    for (const option of termSheetOptions) {
      const list = groups.get(option.group) ?? [];
      list.push(option);
      groups.set(option.group, list);
    }
    return [...groups.entries()];
  }, [termSheetOptions]);

  const createMutation = useMutation({
    mutationFn: () =>
      apiJson<{ count: number }>("/v1/admin/sentence-puzzles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count: 5 }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
    },
  });

  const manualMutation = useMutation({
    mutationFn: () => {
      const distractors = manualDistractors
        .split(/[|;]/)
        .map((part) => part.trim())
        .filter(Boolean);
      return apiJson<AdminSentencePuzzle>("/v1/admin/sentence-puzzles/manual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          english: manualEnglish.trim(),
          hmong: manualHmong.trim(),
          distractors,
          resource_key: manualResourceKey.trim() || null,
          game_type: manualGameType.trim() || null,
        }),
      });
    },
    onSuccess: (puzzle) => {
      setManualEnglish("");
      setManualHmong("");
      setManualDistractors("");
      setManualResourceKey("");
      setManualGameType("");
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      void navigate({
        to: "/admin/sentence-puzzles/$id",
        params: { id: String(puzzle.id) },
      });
    },
  });

  const csvMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      form.append("auto_distractors", autoDistractors ? "true" : "false");
      return apiJson<BulkImportSentencePuzzlesResult>(
        "/v1/admin/sentence-puzzles/bulk-csv",
        { method: "POST", body: form },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });

  const deleteDraftsMutation = useMutation({
    mutationFn: () =>
      apiJson<DeleteDraftSentencePuzzlesResult>(
        "/v1/admin/sentence-puzzles/drafts",
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      setSelectedIds(new Set());
      setDeleteDraftsOpen(false);
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: () =>
      apiJson<DeleteAllSentencePuzzlesResult>(
        "/v1/admin/sentence-puzzles/all",
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      void qc.invalidateQueries({ queryKey: ["admin", "resource-practice"] });
      setSelectedIds(new Set());
      setDeleteAllOpen(false);
    },
  });

  const assignMutation = useMutation({
    mutationFn: (payload: {
      puzzle_ids: number[];
      resource_key: string;
      publish: boolean;
    }) =>
      apiJson<AssignSentencePuzzlesResult>(
        "/v1/admin/sentence-puzzles/assign",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            puzzle_ids: payload.puzzle_ids,
            resource_key: payload.resource_key,
            game_type: GAME_TYPE_BUILD_SENTENCE,
            publish: payload.publish,
          }),
        },
      ),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      void qc.invalidateQueries({ queryKey: ["admin", "resource-practice"] });
      setSelectedIds(new Set());
      setAssignOpen(false);
      setAssignResourceKey("");
      setAssignPublish(false);
      setBankFilter("assigned");
      return result;
    },
  });

  const publishMutation = useMutation({
    mutationFn: (puzzle_ids: number[]) =>
      apiJson<PublishSentencePuzzlesResult>(
        "/v1/admin/sentence-puzzles/publish",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ puzzle_ids }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      void qc.invalidateQueries({ queryKey: ["admin", "resource-practice"] });
      setSelectedIds(new Set());
      setPublishOpen(false);
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (puzzle_ids: number[]) =>
      apiJson<UnpublishSentencePuzzlesResult>(
        "/v1/admin/sentence-puzzles/unpublish",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ puzzle_ids }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "sentence-puzzles"] });
      void qc.invalidateQueries({ queryKey: ["admin", "resource-practice"] });
      setSelectedIds(new Set());
      setUnpublishOpen(false);
    },
  });

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (listQ.isError) {
    return (
      <p className="text-sm text-red-600">{(listQ.error as Error).message}</p>
    );
  }

  const rows = listQ.data ?? [];
  const draftCount = rows.filter((row) => !row.is_published).length;
  const totalCount = rows.length;
  const bankCount = rows.filter((row) => !row.resource_key).length;
  const assignedCount = rows.filter((row) => Boolean(row.resource_key)).length;
  const manualReady =
    manualEnglish.trim().length > 0 && manualHmong.trim().length > 0;

  const visibleRows = rows.filter((row) => {
    if (bankFilter === "bank") return !row.resource_key;
    if (bankFilter === "assigned") return Boolean(row.resource_key);
    return true;
  });

  const visibleIds = visibleRows.map((row) => row.id);
  const selectedVisibleCount = visibleIds.filter((id) =>
    selectedIds.has(id),
  ).length;
  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const selectedCount = selectedIds.size;
  const selectedBankDraftIds = rows
    .filter(
      (row) =>
        selectedIds.has(row.id) && !row.is_published && !row.resource_key,
    )
    .map((row) => row.id);
  const selectedBankDraftCount = selectedBankDraftIds.length;
  const selectedLiveIds = rows
    .filter((row) => selectedIds.has(row.id) && row.is_published)
    .map((row) => row.id);
  const selectedLiveCount = selectedLiveIds.length;

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };

  const openAssignPanel = () => {
    assignMutation.reset();
    setPublishOpen(false);
    setUnpublishOpen(false);
    setAssignOpen(true);
  };

  const assignedSheet = termSheetOptions.find(
    (option) => option.value === assignMutation.data?.resource_key,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Sentence Puzzles</h1>
        <p className="mt-1 text-sm text-gray-600">
          Generate, paste, or CSV-import translation sentence-builder puzzles
          for the mobile app (OTA). Sheet-attached practice is also managed from{" "}
          <Link to="/admin/term-sheets" className="text-primary hover:underline">
            Content → Term Sheets
          </Link>
          .
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="space-y-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              1. Generate with AI
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Creates 5 draft puzzles from vocab/dictionary context via gpt-5.6.
            </p>
          </div>
          <Button
            disabled={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Generating…" : "Generate Drafts"}
          </Button>
          {createMutation.isError ? (
            <p className="text-sm text-red-600">
              {(createMutation.error as Error).message}
            </p>
          ) : null}
          {createMutation.isSuccess ? (
            <p className="text-sm text-green-700">
              Generated {createMutation.data.count} draft
              {createMutation.data.count === 1 ? "" : "s"}.
            </p>
          ) : null}
        </Card>

        <Card className="space-y-3 lg:col-span-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              2. Add manually
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Paste a ready English prompt + Hmong sentence. Tiles and
              punctuation are derived automatically. Distractors are optional
              and left empty unless you add them (separate with | or ;).
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="English prompt"
              value={manualEnglish}
              onChange={(e) => setManualEnglish(e.target.value)}
              placeholder="We go to the store."
            />
            <Input
              label="Hmong sentence"
              value={manualHmong}
              onChange={(e) => setManualHmong(e.target.value)}
              placeholder="Peb mus tom khw."
            />
            <Input
              label="Distractors (optional)"
              value={manualDistractors}
              onChange={(e) => setManualDistractors(e.target.value)}
              placeholder="noj | haus"
            />
            <Input
              label="Resource key (optional)"
              value={manualResourceKey}
              onChange={(e) => setManualResourceKey(e.target.value)}
              placeholder="/(cheatsheets)/asking-questions"
            />
            <Input
              label="Game type (optional)"
              value={manualGameType}
              onChange={(e) => setManualGameType(e.target.value)}
              placeholder="build_sentence"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={!manualReady || manualMutation.isPending}
              onClick={() => manualMutation.mutate()}
            >
              {manualMutation.isPending ? "Saving…" : "Create draft"}
            </Button>
            {manualMutation.isError ? (
              <p className="text-sm text-red-600">
                {(manualMutation.error as Error).message}
              </p>
            ) : null}
          </div>
        </Card>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              3. Bulk upload CSV
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Required columns: <code>english</code>, <code>hmong</code>.
              Optional: <code>distractors</code> (use | or ; — leave blank for
              none, or check “Add distractors” to auto-fill),{" "}
              <code>resource_key</code>, <code>game_type</code>. Max 200 rows.
            </p>
          </div>
          <Button variant="secondary" onClick={downloadCsvTemplate}>
            Download template
          </Button>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            checked={autoDistractors}
            onChange={(e) => setAutoDistractors(e.target.checked)}
            disabled={csvMutation.isPending}
          />
          Add distractors automatically
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="block w-full max-w-md text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) csvMutation.mutate(file);
            }}
          />
          {csvMutation.isPending ? (
            <span className="text-sm text-gray-500">Uploading…</span>
          ) : null}
        </div>
        {csvMutation.isError ? (
          <p className="text-sm text-red-600">
            {(csvMutation.error as Error).message}
          </p>
        ) : null}
        {csvMutation.isSuccess ? (
          <div className="space-y-1 text-sm">
            <p className="text-green-700">
              Imported {csvMutation.data.count} draft
              {csvMutation.data.count === 1 ? "" : "s"}.
            </p>
            {csvMutation.data.errors.length > 0 ? (
              <div className="rounded-xl bg-amber-50 px-3 py-2 text-amber-900">
                <p className="font-medium">
                  Skipped {csvMutation.data.errors.length} row
                  {csvMutation.data.errors.length === 1 ? "" : "s"}:
                </p>
                <ul className="mt-1 list-disc pl-5">
                  {csvMutation.data.errors.slice(0, 8).map((err) => (
                    <li key={`${err.row}-${err.message}`}>
                      Row {err.row}: {err.message}
                    </li>
                  ))}
                  {csvMutation.data.errors.length > 8 ? (
                    <li>
                      …and {csvMutation.data.errors.length - 8} more
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <Button
            variant="danger"
            disabled={
              draftCount === 0 ||
              deleteDraftsMutation.isPending ||
              deleteAllMutation.isPending ||
              listQ.isLoading
            }
            onClick={() => {
              deleteDraftsMutation.reset();
              setDeleteDraftsOpen(true);
            }}
          >
            Delete all drafts
          </Button>
          <Button
            variant="danger"
            disabled={
              totalCount === 0 ||
              deleteAllMutation.isPending ||
              deleteDraftsMutation.isPending ||
              listQ.isLoading
            }
            onClick={() => {
              deleteAllMutation.reset();
              setDeleteAllOpen(true);
            }}
          >
            Delete all sentences
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          Drafts are queued for review. Select bank puzzles and publish them to
          the Build a Sentence game, or open a puzzle to approve it
          individually. Assign selected puzzles to a term sheet to move them
          into that sheet&apos;s Let&apos;s Practice game instead.
        </p>
        {deleteDraftsMutation.isError ? (
          <p className="text-sm text-red-600">
            {(deleteDraftsMutation.error as Error).message}
          </p>
        ) : null}
        {deleteDraftsMutation.isSuccess ? (
          <p className="text-sm text-green-700">
            Deleted {deleteDraftsMutation.data.deleted} draft
            {deleteDraftsMutation.data.deleted === 1 ? "" : "s"}. Live puzzles
            were kept.
          </p>
        ) : null}
        {deleteAllMutation.isError ? (
          <p className="text-sm text-red-600">
            {(deleteAllMutation.error as Error).message}
          </p>
        ) : null}
        {deleteAllMutation.isSuccess ? (
          <p className="text-sm text-green-700">
            Deleted {deleteAllMutation.data.deleted} sentence puzzle
            {deleteAllMutation.data.deleted === 1 ? "" : "s"} (including live).
          </p>
        ) : null}
      </Card>

      <ConfirmDialog
        open={deleteDraftsOpen}
        title="Delete all draft puzzles?"
        message={`This permanently deletes all ${draftCount} draft sentence puzzle${
          draftCount === 1 ? "" : "s"
        }. Live puzzles are not affected. This cannot be undone.`}
        confirmLabel="Delete all drafts"
        confirmVariant="danger"
        loading={deleteDraftsMutation.isPending}
        onCancel={() => {
          if (!deleteDraftsMutation.isPending) {
            setDeleteDraftsOpen(false);
            deleteDraftsMutation.reset();
          }
        }}
        onConfirm={() => deleteDraftsMutation.mutate()}
      />

      <ConfirmDialog
        open={deleteAllOpen}
        title="Delete ALL sentence puzzles?"
        message={`This permanently deletes every sentence puzzle (${totalCount} total), including live ones shown in the app. This cannot be undone.`}
        confirmLabel="Delete all sentences"
        confirmVariant="danger"
        confirmPhrase={DELETE_ALL_CONFIRM_PHRASE}
        confirmPhraseLabel={`Type ${DELETE_ALL_CONFIRM_PHRASE} to confirm`}
        loading={deleteAllMutation.isPending}
        onCancel={() => {
          if (!deleteAllMutation.isPending) {
            setDeleteAllOpen(false);
            deleteAllMutation.reset();
          }
        }}
        onConfirm={() => deleteAllMutation.mutate()}
      />

      <Card className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Puzzle bank
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {bankCount} in general bank · {assignedCount} assigned to term
              sheets · {totalCount} total
            </p>
          </div>
          <div className="flex flex-wrap gap-1 rounded-2xl bg-gray-100 p-1">
            {(
              [
                ["bank", `General bank (${bankCount})`],
                ["assigned", `Assigned (${assignedCount})`],
                ["all", `All (${totalCount})`],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setBankFilter(value);
                  setAssignOpen(false);
                  setPublishOpen(false);
                  setUnpublishOpen(false);
                }}
                className={clsx(
                  "rounded-xl px-3 py-1.5 text-sm font-medium transition",
                  bankFilter === value
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              checked={allVisibleSelected}
              disabled={visibleIds.length === 0}
              onChange={toggleSelectAllVisible}
            />
            Select all shown
          </label>
          <span className="text-sm text-gray-500">
            {selectedCount} selected
          </span>
          <Button
            size="sm"
            disabled={
              selectedBankDraftCount === 0 || publishMutation.isPending
            }
            onClick={() => {
              publishMutation.reset();
              setAssignOpen(false);
              setUnpublishOpen(false);
              setPublishOpen(true);
            }}
          >
            Publish to Build a Sentence
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={selectedCount === 0}
            onClick={openAssignPanel}
          >
            Assign to Term Sheet
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={selectedLiveCount === 0 || unpublishMutation.isPending}
            onClick={() => {
              unpublishMutation.reset();
              setAssignOpen(false);
              setPublishOpen(false);
              setUnpublishOpen(true);
            }}
          >
            Unpublish live
          </Button>
          {selectedCount > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setSelectedIds(new Set());
                setAssignOpen(false);
                setPublishOpen(false);
                setUnpublishOpen(false);
              }}
            >
              Clear selection
            </Button>
          ) : null}
        </div>
        {selectedCount > 0 && selectedBankDraftCount === 0 ? (
          <p className="text-xs text-gray-500">
            Publish to Build a Sentence only applies to unpublished bank
            drafts. Assigned drafts are published from that term sheet&apos;s
            practice page (or assign + publish now).
          </p>
        ) : selectedCount > selectedBankDraftCount &&
          selectedBankDraftCount > 0 ? (
          <p className="text-xs text-gray-500">
            {selectedBankDraftCount} unpublished bank draft
            {selectedBankDraftCount === 1 ? "" : "s"} will be published. Assigned
            or already-live rows are left unchanged.
          </p>
        ) : null}

        {assignOpen ? (
          <div className="space-y-3 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
            <p className="text-sm text-gray-800">
              Assign {selectedCount} puzzle{selectedCount === 1 ? "" : "s"} to a
              term sheet practice game. They will leave the general Build a
              Sentence bank.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block min-w-[16rem] flex-1 text-sm">
                <span className="mb-1 block font-medium text-gray-700">
                  Term sheet
                </span>
                <select
                  className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={assignResourceKey}
                  onChange={(e) => setAssignResourceKey(e.target.value)}
                  disabled={
                    termSheetOptions.length === 0 || assignMutation.isPending
                  }
                >
                  <option value="">
                    {termSheetOptions.length === 0
                      ? "Loading term sheets…"
                      : "Select a term sheet…"}
                  </option>
                  {optionGroups.map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label className="inline-flex items-center gap-2 pb-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  checked={assignPublish}
                  onChange={(e) => setAssignPublish(e.target.checked)}
                  disabled={assignMutation.isPending}
                />
                Publish now (shows Let’s Practice in the app)
              </label>
              <Button
                disabled={
                  selectedCount === 0 ||
                  !assignResourceKey ||
                  assignMutation.isPending
                }
                onClick={() =>
                  assignMutation.mutate({
                    puzzle_ids: [...selectedIds],
                    resource_key: assignResourceKey,
                    publish: assignPublish,
                  })
                }
              >
                {assignMutation.isPending ? "Assigning…" : "Confirm assign"}
              </Button>
              <Button
                variant="ghost"
                disabled={assignMutation.isPending}
                onClick={() => {
                  setAssignOpen(false);
                  assignMutation.reset();
                }}
              >
                Cancel
              </Button>
            </div>
            {cloudSheetsQ.isError || staticSheetsQ.isError ? (
              <p className="text-sm text-red-600">
                Could not load term sheets.{" "}
                {(
                  (cloudSheetsQ.error ?? staticSheetsQ.error) as Error
                )?.message}
              </p>
            ) : null}
            {assignMutation.isError ? (
              <p className="text-sm text-red-600">
                {getApiErrorMessage(
                  assignMutation.error,
                  "Could not assign puzzles. Try again.",
                )}
              </p>
            ) : null}
          </div>
        ) : null}

        <ConfirmDialog
          open={publishOpen}
          title="Publish to Build a Sentence?"
          message={`This publishes ${selectedBankDraftCount} bank draft${
            selectedBankDraftCount === 1 ? "" : "s"
          } live for the Build a Sentence game. They stay in the general bank and are not assigned to a term sheet.`}
          confirmLabel="Publish to Build a Sentence"
          loading={publishMutation.isPending}
          onCancel={() => {
            if (!publishMutation.isPending) {
              setPublishOpen(false);
              publishMutation.reset();
            }
          }}
          onConfirm={() => publishMutation.mutate(selectedBankDraftIds)}
        />

        <ConfirmDialog
          open={unpublishOpen}
          title="Unpublish selected live puzzles?"
          message={`This moves ${selectedLiveCount} live puzzle${
            selectedLiveCount === 1 ? "" : "s"
          } back to draft. Bank puzzles leave the Build a Sentence game; assigned puzzles stay on their term sheet but Let’s Practice hides them until they are published again.`}
          confirmLabel="Unpublish live"
          confirmVariant="danger"
          loading={unpublishMutation.isPending}
          onCancel={() => {
            if (!unpublishMutation.isPending) {
              setUnpublishOpen(false);
              unpublishMutation.reset();
            }
          }}
          onConfirm={() => unpublishMutation.mutate(selectedLiveIds)}
        />

        {publishMutation.isError ? (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(
              publishMutation.error,
              "Could not publish puzzles. Try again.",
            )}
          </p>
        ) : null}
        {publishMutation.isSuccess ? (
          <div className="space-y-1 text-sm text-green-700">
            <p>
              Published {publishMutation.data.published} puzzle
              {publishMutation.data.published === 1 ? "" : "s"} to the Build a
              Sentence game
              {publishMutation.data.skipped_ids.length
                ? ` · ${publishMutation.data.skipped_ids.length} already live`
                : ""}
              .
            </p>
            {publishMutation.data.errors.length ? (
              <p className="text-amber-800">
                {publishMutation.data.errors.length} could not be published
                {publishMutation.data.errors
                  .slice(0, 3)
                  .map((err) => ` · #${err.row}: ${err.message}`)
                  .join("")}
                {publishMutation.data.errors.length > 3
                  ? ` · and ${publishMutation.data.errors.length - 3} more`
                  : ""}
                . Open those puzzles to fix validation errors.
              </p>
            ) : null}
          </div>
        ) : null}

        {unpublishMutation.isError ? (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(
              unpublishMutation.error,
              "Could not unpublish puzzles. Try again.",
            )}
          </p>
        ) : null}
        {unpublishMutation.isSuccess ? (
          <p className="text-sm text-green-700">
            Unpublished {unpublishMutation.data.unpublished} puzzle
            {unpublishMutation.data.unpublished === 1 ? "" : "s"}
            {unpublishMutation.data.skipped_ids.length
              ? ` · ${unpublishMutation.data.skipped_ids.length} already draft`
              : ""}
            .
          </p>
        ) : null}

        {assignMutation.isSuccess ? (
          <div className="space-y-1 text-sm text-green-700">
            <p>
              Assigned {assignMutation.data.updated} puzzle
              {assignMutation.data.updated === 1 ? "" : "s"} to{" "}
              <strong>{assignedSheet?.label ?? assignMutation.data.resource_key}</strong>
              {assignMutation.data.published_ids?.length
                ? ` and published ${assignMutation.data.published_ids.length}`
                : ""}
              . They now appear under that term sheet&apos;s practice game.
            </p>
            {assignMutation.data.publish_errors?.length ? (
              <p className="text-amber-800">
                {assignMutation.data.publish_errors.length} could not be
                published — open them from the practice page and fix validation
                errors.
              </p>
            ) : null}
            <p>
              <Link
                to="/admin/term-sheets/practice"
                search={termSheetPracticeSearch({
                  resourceKey: assignMutation.data.resource_key,
                  title: assignedSheet?.label ?? assignMutation.data.resource_key,
                  kind: assignedSheet?.group.startsWith("Cloud")
                    ? "cloud"
                    : "static",
                })}
                className="font-medium text-primary hover:underline"
              >
                Open practice page →
              </Link>
            </p>
          </div>
        ) : null}
      </Card>

      {visibleRows.length === 0 ? (
        <EmptyState
          title={
            bankFilter === "bank"
              ? "No puzzles in the general bank"
              : bankFilter === "assigned"
                ? "No puzzles assigned to term sheets"
                : "No sentence puzzles yet"
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleRows.map((row) => {
            const selected = selectedIds.has(row.id);
            return (
              <div
                key={row.id}
                className={clsx(
                  "relative rounded-[22px]",
                  selected && "ring-2 ring-primary ring-offset-2",
                )}
              >
                <label
                  className="absolute left-4 top-4 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-xl bg-white/95 shadow-sm"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={selected}
                    onChange={() => toggleSelected(row.id)}
                    aria-label={`Select ${row.prompt_en || row.slug}`}
                  />
                </label>
                <Link
                  to="/admin/sentence-puzzles/$id"
                  params={{ id: String(row.id) }}
                  className={clsx(
                    "group block rounded-[22px] focus:outline-none",
                    "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                  )}
                >
                  <Card
                    className={clsx(
                      "h-full pl-12 transition",
                      "group-hover:border-primary/20 group-hover:shadow-[0_14px_24px_rgba(0,0,0,0.08)]",
                      "group-active:scale-[0.99]",
                    )}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h2 className="text-lg font-medium text-gray-900">
                        {row.prompt_en || row.target_hmong || row.slug}
                      </h2>
                      <div className="flex flex-wrap gap-1">
                        <Badge
                          variant={row.is_published ? "success" : "neutral"}
                        >
                          {row.is_published ? "Live" : "Draft"}
                        </Badge>
                        {row.resource_key ? (
                          <Badge variant="neutral">Assigned</Badge>
                        ) : (
                          <Badge variant="neutral">Bank</Badge>
                        )}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-700">
                      {row.target_hmong}
                    </p>
                    <p className="text-sm text-gray-500">
                      Generated {formatAdminDateTime(row.generated_at)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {row.queue_order != null
                        ? `Queue #${row.queue_order}`
                        : "Not approved yet"}{" "}
                      · {row.tile_count} tiles
                      {row.resource_key ? ` · ${row.resource_key}` : ""}
                      {row.game_type ? ` · ${row.game_type}` : ""}
                    </p>
                    <p className="mt-3 text-sm font-medium text-primary group-hover:underline">
                      Review puzzle →
                    </p>
                  </Card>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/sentence-puzzles")({
  component: function SentencePuzzlesRoute() {
    const matches = useMatches();
    const onDetail = matches.some((m) =>
      m.routeId.includes("sentence-puzzles/$id"),
    );
    if (onDetail) return <Outlet />;
    return <SentencePuzzlesList />;
  },
});
